import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import Page from "./Page";
import Card from "../layout/Card";
import DataTable from "../elements/DataTable";

import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const PayReceipt = () => {
  const { executeAction, jsonToCSV } = useData();
  const { showMessage } = usePage();

  const formatCurrency = (value) => {
    const numeric = Number(value);
    const safeValue = Number.isFinite(numeric) ? numeric : 0;

    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeValue);
  };

  const [loading, setLoading] = useState(false);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState("");
  const [selectedPayPeriodName, setSelectedPayPeriodName] = useState("");
  const [records, setRecords] = useState([]);

  const columns = useMemo(
    () => [
      { name: "scheduleDate", label: "Schedule Date", width: "130px" },
      { name: "projectName", label: "Project Name", width: "220px" },
      { name: "storeLocation", label: "Store Location", width: "220px" },
      { name: "storeNumber", label: "Store Number", width: "140px" },
      { name: "address", label: "Address", width: "320px" },
      {
        name: "callDurationPaid",
        label: "Call Duration Paid",
        width: "160px",
      },
      {
        name: "timeReimbursed",
        label: "Time reimbursed $",
        width: "170px",
        render: (row) => formatCurrency(row.timeReimbursed),
      },
      {
        name: "totalMileageReimbursed",
        label: "Total Mileage reimbursed $",
        width: "220px",
        render: (row) => formatCurrency(row.totalMileageReimbursed),
      },
    ],
    [],
  );

  const loadReceipt = async (payPeriodId = "") => {
    setLoading(true);

    const results = await executeAction({
      entityName: "PayTransaction",
      action: "getRepPayReceipt",
      data: payPeriodId ? { payPeriodId } : {},
    });

    if (results?.isSuccessful && results.data) {
      const periods = Array.isArray(results.data.payPeriods)
        ? results.data.payPeriods
        : [];
      const receiptRows = Array.isArray(results.data.records)
        ? results.data.records
        : [];

      setPayPeriods(periods);
      setSelectedPayPeriodId(String(results.data.selectedPayPeriodId || ""));
      setSelectedPayPeriodName(results.data.selectedPayPeriodName || "");
      setRecords(receiptRows);

      if (periods.length === 0) {
        showMessage("No closed pay periods are available.", "warning");
      }
    } else {
      setRecords([]);
      showMessage(results?.message || "Unable to load pay receipt.", "error");
    }

    setLoading(false);
  };

  useEffect(() => {
    loadReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePayPeriodChange = async (event) => {
    const payPeriodId = String(event.target.value || "");
    if (!payPeriodId) return;
    await loadReceipt(payPeriodId);
  };

  const handleExportCsv = () => {
    if (!Array.isArray(records) || records.length === 0) {
      showMessage("No records to export.", "error");
      return;
    }

    const exportRows = records.map((row) => ({
      "Schedule Date": row.scheduleDate || "",
      "Project Name": row.projectName || "",
      "Store Location": row.storeLocation || "",
      "Store Number": row.storeNumber || "",
      Address: row.address || "",
      "Call Duration Paid": row.callDurationPaid ?? "",
      "Time reimbursed $": formatCurrency(row.timeReimbursed),
      "Total Mileage reimbursed $": formatCurrency(row.totalMileageReimbursed),
    }));

    const csv = jsonToCSV(exportRows);
    if (!csv) {
      showMessage("Unable to generate CSV.", "error");
      return;
    }

    const fileNamePart = String(selectedPayPeriodName || "pay-period")
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `pay-receipt-${fileNamePart || "pay-period"}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Page pageName="payReceipt">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Card title="Pay Receipt">
          <Stack
            spacing={2}
            sx={{ p: 2 }}
          >
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <FormControl
                size="small"
                sx={{ minWidth: 280 }}
              >
                <InputLabel>Pay Period</InputLabel>
                <Select
                  value={selectedPayPeriodId}
                  label="Pay Period"
                  onChange={handlePayPeriodChange}
                  disabled={loading || payPeriods.length === 0}
                >
                  {payPeriods.map((period) => (
                    <MenuItem
                      key={period.id}
                      value={period.id}
                    >
                      {period.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                onClick={handleExportCsv}
                disabled={loading || records.length === 0}
              >
                Export CSV
              </Button>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              {selectedPayPeriodName
                ? `Showing ${records.length} call(s) for ${selectedPayPeriodName}`
                : "Select a closed pay period"}
            </Typography>
          </Stack>
        </Card>

        <Card title={`Calls Included (${records.length})`}>
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : records.length === 0 ? (
              <Typography>No records found for this pay period.</Typography>
            ) : (
              <DataTable
                data={records}
                columns={columns}
                allowSort
                tableHeight="62vh"
              />
            )}
          </Box>
        </Card>
      </Box>
    </Page>
  );
};

export default PayReceipt;
